import React, { useMemo } from "react";

/**
 * Chart stand-ins. Only TableChart is actually used by the dashboard; the
 * others exist so that adding a chart in the nerdlet does not silently break
 * the standalone bundle (a missing named export is a hard Rollup error).
 */

const cx = (...parts) => parts.filter(Boolean).join(" ");

/** Flattens SDK chart data back into tabular rows. */
export function chartDataToRows(data) {
  if (!Array.isArray(data)) return { columns: [], rows: [] };

  const rows = [];

  data.forEach((series) => {
    const facetColumns = {};
    (series?.metadata?.groups || [])
      .filter((group) => group.type === "facet")
      .forEach((group) => {
        facetColumns[group.name] = group.value;
      });

    const points = series?.data?.length ? series.data : [{}];
    points.forEach((point) => {
      // `y` is a synthetic key added during translation, not a real column.
      const { y, x, ...rest } = point;
      rows.push({ ...facetColumns, ...rest });
    });
  });

  const columns = [];
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (!columns.includes(key)) columns.push(key);
    });
  });

  return { columns, rows };
}

function formatCell(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") {
    return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
  }
  if (typeof value === "boolean") return String(value);
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function TableChart({ data, className, fullWidth, fullHeight, accountId, query, ...rest }) {
  const { columns, rows } = useMemo(() => chartDataToRows(data), [data]);

  if (!rows.length) {
    return <div className="nr1c-chart-empty">No data available.</div>;
  }

  return (
    <div className={cx("nr1c-table-chart", className)} {...rest}>
      <table>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column} scope="col">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {columns.map((column) => (
                <td key={column}>{formatCell(row[column])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function unsupportedChart(name) {
  const Component = ({ className }) => (
    <div className={cx("nr1c-chart-empty", className)}>
      {name} is not implemented in the standalone build.
    </div>
  );
  Component.displayName = name;
  return Component;
}

export const LineChart = unsupportedChart("LineChart");
export const AreaChart = unsupportedChart("AreaChart");
export const BarChart = unsupportedChart("BarChart");
export const PieChart = unsupportedChart("PieChart");
export const BillboardChart = unsupportedChart("BillboardChart");
export const SparklineChart = unsupportedChart("SparklineChart");
export const HeatmapChart = unsupportedChart("HeatmapChart");
export const JsonChart = unsupportedChart("JsonChart");
